/**
 * PEARSON PROPRIETARY AND CONFIDENTIAL INFORMATION SUBJECT TO NDA
 * Copyright © 2025 Pearson Education, Inc. All Rights Reserved.
 *
 * NOTICE: All information contained herein is, and remains
 * the property of Pearson Education, Inc.  The intellectual and technical concepts contained
 * herein are proprietary to Pearson Education, Inc. and may be covered by U.S. and Foreign Patents,
 * patent applications, and are protected by trade secret or copyright law.
 * Dissemination of this information, reproduction of this material, and copying or distribution of this software
 * is strictly forbidden unless prior written permission is obtained
 * from Pearson Education, Inc.
 */

/**
 * DeleteButton
 *
 * @file DeleteButton.jsx
 * @author Ujjwal Sana
 * @since 0.0.2
 */


import React from 'react';
import PropTypes from 'prop-types';
import { makeStyles } from '@material-ui/core/styles';
import { IconButton, Badge } from '@material-ui/core';
import TooltipWrapper from '../tooltip/TooltipWrapper';
import Icon from '../icons/Icon';
import mixins from '../../assets/styles/mixins.scss';

const useStyles = makeStyles(() => ({
  badge: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: `${mixins.badgeBgColoe}`,
    color: `${mixins.accentLightRed}`,
    fontFamily: mixins.fontFamilyPlusSans,
    fontSize: 12,
    padding: '0 4px 2px 4px',
    fontWeight: 600,
    textAlign: 'center',
    lineHeight: '20px'
  },
  iconButton: {
    color: mixins.navyColor,
    fontFeatureSettings: '\'liga\' off',
    fontFamily: mixins.fontFamilyPlusSans,
    fontSize: mixins.fontSize14,
    fontStyle: 'normal',
    fontWeight: mixins.fontWeight500,
    lineHeight: '22px',
    padding: 8,
    borderRadius: '1000px',
    minWidth: 'unset',

    '&:hover': {
      background: mixins.secondaryButtonHoverColor
    },
    '& > span': {
      minWidth: 'unset',
      margin: 0,
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      padding: '0px 8px 2px 8px'
    },
    '&.Mui-disabled': {
      color: mixins.disabledTextColor,
      opacity: 'unset',
      pointerEvents: 'unset',

      '&:hover': {
        background: mixins.secondaryButtonHoverColor,
        color: 'inherit'
      }
    }
  },
  iconOnlyButton: {
    '& > span': {
      padding: 0
    }
  },
  circleAroundIcon: {
    padding: 12,
    borderRadius: '50%',
    border: `1px solid ${mixins.secondaryButtonHoverColor}`,
    boxSizing: 'content-box'
  },
  icon: {
    width: '16px',
    height: '16px'
  }
}));

const DeleteButton = ({
  tooltipText,
  badgeCount,
  iconName,
  disabled,
  callbackFunc
}) => {
  const classes = useStyles();


  return (
    <TooltipWrapper tooltip={tooltipText || ''} placement="top">
      <Badge
        badgeContent={badgeCount || 0}
        showZero={false}
        color="secondary"
        overlap="circular"
        classes={{
          root: classes.badgeRoot,
          badge: classes.badge
        }}
      >
        <IconButton className={`${classes.iconButton} ${classes.iconOnlyButton} ${badgeCount > 0 ? classes.circleAroundIcon : ''}`} onClick={(event) => (disabled ? () => {} : callbackFunc(event))} disabled={disabled}>
          <Icon name={iconName} className={classes.icon} />
        </IconButton>
      </Badge>
    </TooltipWrapper>
  );
};

DeleteButton.propTypes = {
  tooltipText: PropTypes.string.isRequired,
  badgeCount: PropTypes.number,
  iconName: PropTypes.string.isRequired,
  disabled: PropTypes.bool,
  callbackFunc: PropTypes.func.isRequired
};

DeleteButton.defaultProps = {
  badgeCount: 0,
  disabled: true
};

export default DeleteButton;
